/**
 * Return a safe `data:` URL src for a base64 string possibly carrying
 * its own `data:image/...;base64,` prefix. Null/empty => null so the
 * caller can fall back to a placeholder. The MIME is auto-detected
 * from the base64 magic bytes (JPEG/PNG/GIF/WebP) so JPEGs produced
 * by compressImageFile keep a correct `data:image/jpeg;...` prefix.
 */
export function b64ToSrc(b64, mime) {
    if (!b64 || typeof b64 !== "string") return null;
    const clean = b64.replace(/^data:image\/[a-z0-9+.-]+;base64,/i, "");
    if (!mime) {
        if (clean.startsWith("/9j/")) mime = "image/jpeg";
        else if (clean.startsWith("iVBOR")) mime = "image/png";
        else if (clean.startsWith("R0lGOD")) mime = "image/gif";
        else if (clean.startsWith("UklGR")) mime = "image/webp";
        else mime = "image/png";
    }
    return `data:${mime};base64,${clean}`;
}

/**
 * Client-side image compression — resize the longest side to `maxSide`
 * and re-encode as high-quality JPEG. Designed for flyers and menu
 * photos: keeps visual fidelity while drastically shrinking payload
 * size before upload (especially on mobile 4G).
 *
 * Returns a base64 string (without the `data:` prefix) ready to send
 * to the backend, or the original base64 if the file is already small
 * enough or compression fails.
 */
export async function compressImageFile(file, {
    maxSide = 1600,
    quality = 0.88,
    skipBelowBytes = 500 * 1024, // < 500 KB → keep as-is
} = {}) {
    if (!file) return null;
    const fileToB64 = (f) => new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1]);
        r.onerror = reject;
        r.readAsDataURL(f);
    });
    // Small enough — don't re-encode (avoids generation loss, keeps PNG transparency).
    if (file.size <= skipBelowBytes) {
        return await fileToB64(file);
    }
    try {
        const dataUrl = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.onerror = reject;
            r.readAsDataURL(file);
        });
        const img = await new Promise((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = reject;
            i.src = dataUrl;
        });
        const w0 = img.naturalWidth || img.width;
        const h0 = img.naturalHeight || img.height;
        if (!w0 || !h0) return await fileToB64(file);
        const ratio = Math.min(1, maxSide / Math.max(w0, h0));
        const w = Math.round(w0 * ratio);
        const h = Math.round(h0 * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return await fileToB64(file);
        // White background to avoid black halos on JPEG when source is PNG
        // with transparency (typical for flyers exported from Canva, etc.)
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        const out = canvas.toDataURL("image/jpeg", quality);
        return out.split(",")[1];
    } catch {
        return await fileToB64(file);
    }
}
